import React, { useCallback, useEffect, useState } from 'react';
import { Modal, Table, Badge, Spinner, Alert, Row, Col, Card } from 'react-bootstrap';
import { taskDurationAPI } from '../../services';

/**
 * #142431 — "Xem lịch sử": trung bình thực tế + từng lượt nhân viên báo cáo cho 1 MẪU task.
 *
 * Task chưa ai báo thì hiện đúng chữ "Chưa có dữ liệu khảo sát" — KHÔNG hiện "0 phút", vì 0 phút
 * đọc như là dữ liệu đã mất chứ không phải chưa có (yêu cầu rõ trong đặc tả).
 */
export default function TaskDurationHistoryModal({ task, onHide }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    if (!task?.id) return;
    try {
      setLoading(true);
      setError(null);
      const res = await taskDurationAPI.getTaskDetail(task.id);
      setData(res?.data || res);
    } catch (err) {
      console.error('Error loading task duration history:', err);
      setError('Không tải được lịch sử khảo sát thời gian');
    } finally {
      setLoading(false);
    }
  }, [task]);

  useEffect(() => {
    load();
  }, [load]);

  const stats = data?.stats;
  const reports = data?.reports || [];

  const formatDateTime = (value) => {
    if (!value) return '—';
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('vi-VN');
  };

  return (
    <Modal show={!!task} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: 18 }}>
          Thời gian hoàn thành thực tế — {task?.name}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading && (
          <div className="text-center py-4">
            <Spinner animation="border" />
          </div>
        )}
        {!loading && error && <Alert variant="danger">{error}</Alert>}

        {!loading && !error && data && (
          <>
            <Row className="g-2 mb-3">
              <Col md={4}>
                <Card body className="text-center">
                  <div className="text-muted small">TB thực tế (nhân viên khảo sát)</div>
                  <div className="fs-4 fw-semibold" data-testid="duration-average">
                    {stats?.average_minutes != null ? (
                      `${stats.average_minutes} phút`
                    ) : (
                      <span className="text-muted fs-6">
                        {stats?.status === 'NO_DATA'
                          ? 'Chưa có dữ liệu khảo sát'
                          : `Chưa đủ mẫu (${stats?.valid_samples ?? 0}/${stats?.min_samples ?? 3})`}
                      </span>
                    )}
                  </div>
                </Card>
              </Col>
              <Col md={4}>
                <Card body className="text-center">
                  <div className="text-muted small">Số mẫu</div>
                  <div className="fs-4 fw-semibold">{stats?.total_samples ?? 0}</div>
                  <div className="text-muted small">
                    {stats?.valid_samples ?? 0} hợp lệ · {stats?.outlier_samples ?? 0} bị loại
                  </div>
                </Card>
              </Col>
              <Col md={4}>
                <Card body className="text-center">
                  <div className="text-muted small">Ước lượng khai tay</div>
                  <div className="fs-4 fw-semibold">
                    {data.declared_estimated_minutes != null ? (
                      `${data.declared_estimated_minutes} phút`
                    ) : (
                      <span className="text-muted fs-6">Chưa khai</span>
                    )}
                  </div>
                </Card>
              </Col>
            </Row>

            {reports.length === 0 ? (
              <Alert variant="secondary" data-testid="duration-empty">
                Chưa có dữ liệu khảo sát — chưa nhân viên nào báo thời gian cho việc này.
                Task hoàn thành trước khi có tính năng khảo sát sẽ không có số liệu.
              </Alert>
            ) : (
              <Table responsive hover size="sm" className="align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Nhân viên</th>
                    <th>Chi nhánh</th>
                    <th className="text-end">Số phút</th>
                    <th>Ca</th>
                    <th>Thời điểm báo</th>
                  </tr>
                </thead>
                <tbody>
                  {reports.map((r) => (
                    <tr key={r.id} className={r.is_outlier ? 'text-muted' : undefined}>
                      <td>{r.employee_name || r.employee_id}</td>
                      <td>{r.branch_name || <span className="text-muted">—</span>}</td>
                      <td className="text-end">
                        {r.minutes}p{' '}
                        {r.is_outlier && (
                          <Badge bg="warning" text="dark" title="Nằm ngoài khoảng hợp lý so với các mẫu khác nên không tính vào trung bình">
                            bị loại
                          </Badge>
                        )}
                      </td>
                      <td>{r.shift_name || <span className="text-muted">—</span>}</td>
                      <td>{formatDateTime(r.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </>
        )}
      </Modal.Body>
    </Modal>
  );
}
